<template>
  <v-container fluid>
    <PageTitle id="pageTitle"
               icon="fal fa-fw fa-paper-plane"
               style="position: sticky; top: 64px; z-index: 10; background: #f7f4f3;">
      Send Emails
      <template v-slot:buttons>
        <v-dialog v-model="previewDialog"
                  persistent
                  width="660px"
                  @click:outside="closePreviewDialog">
          <template v-slot:activator="{on, attrs}">
            <v-btn @click.prevent="previewEmail()"
                   :disabled="isFormValid === false"
                   color="primary"
                   class="mr-2"
                   v-bind="attrs"
                   v-on="on">
              <v-icon left>
                fas fa-fw fa-eye
              </v-icon>
              Preview Email
            </v-btn>
          </template>
          <template v-slot:default>
            <v-card v-if="isPreviewValid"
                    flat
                    tile>
              <v-card-title class="primary white--text px-6 d-flex justify-space-between align-center">
                <div>
                  Preview Email
                </div>
                <v-btn @click.prevent="closePreviewDialog"
                       outlined
                       dark>
                  Close
                </v-btn>
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text v-if="previewContacts.length > 1"
                           class="pt-4 pb-2 px-6">
                <v-select v-model="preview_contact"
                          label="Preview Recipient"
                          :items="previewContacts"
                          return-object
                          outlined
                          dense
                          item-value="id"
                          item-text="name_email"
                          hide-details
                          @change="previewEmail">
                </v-select>
              </v-card-text>
              <v-card-text v-if="form.busy"
                           style="height: 500px;">
                <div class="d-flex justify-center align-center fill-height">
                  <v-progress-circular indeterminate
                                       size="60">
                  </v-progress-circular>
                </div>
              </v-card-text>
              <template v-else>
                <v-card-subtitle class="pb-1">
                  From: {{ preview_from_name }} <{{ preview_from_address }}><br/>
                  Reply-To: {{ user.email }}<br/>
                  To: {{ preview_contact.greeting }} <{{ preview_contact.email }}><br/>
                  Subject: {{ preview_subject }}
                </v-card-subtitle>
                <v-divider></v-divider>
                <v-card-text class="pl-6 pr-4"
                             style="max-height: 400px; overflow-y: auto;">
                  <ErrorAlert v-if="preview_error"
                              :error="preview_error">
                  </ErrorAlert>
                  <div v-else-if="preview_image">
                    <v-img :src="previewImageURI"
                           width="600"
                           alt="Preview"/>
                  </div>
                  <iframe v-else-if="previewDataURI"
                          :src="previewDataURI"
                          style="width: 100%; height: 400px; overflow-y: auto; border: 1px solid #cccccc;">
                  </iframe>
                </v-card-text>
              </template>
            </v-card>
          </template>
        </v-dialog>
        <v-btn @click.prevent="sendNow"
               :disabled="form.busy || isFormValid === false"
               color="primary"
               class="mr-2">
          <v-icon left>
            fas fa-fw fa-paper-plane
          </v-icon>
          Send
        </v-btn>
        <v-dialog v-model="scheduleDialog"
                  persistent
                  width="660px">
          <template v-slot:activator="{on, attrs}">
            <v-btn color="primary"
                   :disabled="form.busy || isFormValid === false"
                   v-bind="attrs"
                   v-on="on"
                   @click="openScheduleSendDialog">
              <v-icon left>
                fas fa-fw fa-clock
              </v-icon>
              Schedule
            </v-btn>
          </template>
          <template v-slot:default>
            <v-card flat
                    tile>
              <v-card-title>
                Schedule send to {{ totalRecipients | pluralize('recipient', true) }}
              </v-card-title>
              <v-card-subtitle>
                {{ scheduledTimeZone }}
              </v-card-subtitle>
              <v-card-text class="pb-0">
                <v-row>
                  <v-col cols="6">
                    <v-select v-model="form.scheduled_date"
                              label="Date"
                              outlined
                              :items="scheduleDates"
                              item-value="id"
                              item-text="text"
                              required
                              @change="updateScheduledTime"
                              hide-details>
                    </v-select>
                  </v-col>
                  <v-col cols="6">
                    <v-select v-model="form.scheduled_time"
                              label="Time"
                              outlined
                              :disabled="!form.scheduled_date"
                              :items="scheduleTimes"
                              item-value="id"
                              item-text="text"
                              required
                              hide-details>
                    </v-select>
                  </v-col>
                </v-row>

                <p class="text-caption my-2">
                  You can schedule an email up to 90 days in the future.
                </p>

                <SendFailureNotificationsCheckbox :form="form">
                </SendFailureNotificationsCheckbox>

                <v-alert v-if="scheduleError"
                         type="error"
                         tile
                         class="mb-2">
                  {{ scheduleError.message ?? scheduleError }}
                </v-alert>
              </v-card-text>
              <v-card-actions>
                <v-btn @click.prevent="scheduleSend"
                       :disabled="form.busy || isScheduleFormValid === false"
                       color="primary">
                  Schedule
                </v-btn>
                <v-btn @click.prevent="cancelSchedule"
                       text>
                  Cancel
                </v-btn>
              </v-card-actions>
            </v-card>
          </template>
        </v-dialog>
      </template>
    </PageTitle>

    <v-card class="mb-4"
            tile
            :loading="templatesLoading"
            title="Choose an Email Template">
      <v-card-title>
        <v-icon color="primary"
                class="mr-2">
          fas fa-fw fa-envelope-open-text
        </v-icon>
        Choose an Email Template
        <div v-if="!template && !templatesLoading">
          <v-spacer></v-spacer>
          <v-text-field v-model="searchTemplates"
                        label="Search templates"
                        outlined
                        dense
                        hide-details>
          </v-text-field>
        </div>
      </v-card-title>
      <v-card-text>
        <v-alert v-if="template && mustUseTemplate"
                 type="info">
          You're required to use the <strong>{{ template.name }}</strong> template when sharing <strong>{{ presentation.name }}</strong>.
        </v-alert>
        <div v-else-if="template">
          <div class="d-flex align-center">
            <div @click.prevent="deselectTemplate"
                 class="text-h6 font-weight-bold">
              {{ template.name }}
            </div>
            <v-btn @click.prevent="deselectTemplate"
                   color="error"
                   class="ml-2"
                   icon>
              <v-icon>
                fas fa-fw fa-times-circle
              </v-icon>
            </v-btn>
          </div>
        </div>
        <div v-else-if="filteredTemplates.length > 0">
          <v-data-table :headers="headers"
                        :items="filteredTemplates"
                        :search="searchTemplates"
                        dense
                        :fixed-header="!!tableHeight"
                        :height="tableHeight"
                        :items-per-page="-1"
                        :hide-default-footer="true"
                        class="border">
            <template v-slot:item.name="{item: t}">
              <a @click.prevent="selectTemplate(t)">
                {{ t.name }}
              </a>
            </template>
            <template v-slot:item.expires_at="{item: t}">
              <div v-if="t.expires_at"
                   class="text-nowrap">
                {{ t.expires_at | datetime('YYYY-MM-DD') }}
              </div>
            </template>
          </v-data-table>

          <v-autocomplete v-if="1===2"
                          v-model="template"
                          label="Select an email template"
                          placeholder="Search for the email template by name or subject."
                          :disabled="templatesLoading"
                          :loading="templatesLoading"
                          :items="filteredTemplates"
                          item-value="id"
                          item-text="name_and_subject"
                          return-object
                          outlined
                          dense
                          autocomplete="off"
                          aria-autocomplete="list"
                          required
                          clearable
                          hide-details
                          @change="selectTemplate"
                          @click:clear="deselectTemplate">
            <template v-slot:item="{item:template, on, attrs}">
              <v-list-item v-on="on"
                           v-bind="attrs"
                           three-line>
                <v-list-item-content>
                  <v-list-item-title>
                    Name: {{ template.name }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    Subject: {{ template.subject }}
                  </v-list-item-subtitle>
                  <v-list-item-subtitle v-if="template.is_english === false">
                    Language: {{ template.locale_name }}
                  </v-list-item-subtitle>
                  <v-list-item-subtitle v-if="template.expires_at">
                    Expires: {{ template.expires_at | datetime('MMMM D, YYYY', false) }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-autocomplete>
        </div>

        <EmailTemplateInstructionsAlert :template="template"
                                        class="mt-4 mb-0">
        </EmailTemplateInstructionsAlert>
      </v-card-text>
    </v-card>
    <div id="choose-an-email-template"></div>

    <RecentTemplatesList v-if="!template"
                         :limit="5"
                         @selected="selectTemplate">
    </RecentTemplatesList>
    <div id="recent-template-list"></div>

    <div v-if="template">
      <v-card class="mb-4">
        <v-card-title>
          <v-icon color="primary"
                  class="mr-2">
            fas fa-fw fa-user-plus
          </v-icon>
          Select Recipients
        </v-card-title>
        <v-card-text class="pb-2">
          <v-tabs v-model="recipientsTab"
                  slider-color="primary"
                  :slider-size="4"
                  class="mb-6">
            <v-tab>
              Individual Contacts
            </v-tab>
            <v-tab>
              From a List
            </v-tab>
          </v-tabs>
          <v-tabs-items v-model="recipientsTab"
                        class="pt-2">

            <v-tab-item :value="0"
                        :transition="false"
                        :reverse-transition="false">
              <v-autocomplete v-model="form.contact"
                              ref="contactCombo"
                              :disabled="form.busy || addingContact"
                              label="Search your contacts"
                              :placeholder="canCreateContact ? 'Search for your contacts or add a new one.' : 'Search for your contacts.'"
                              outlined
                              dense
                              autocomplete="off"
                              aria-autocomplete="list"
                              :search-input.sync="search"
                              @focus="clearContactErrors"
                              @change="checkContact"
                              cache-items
                              :items="filteredContacts"
                              item-value="id"
                              item-text="name_email_company_record_type"
                              return-object
                              :error-messages="form.errors.get('contact')"
                              :hide-no-data="isSearchEmpty">
                <template v-slot:item="{item: contact, on, attrs}">
                  <v-list-item v-bind="attrs"
                               v-on="on"
                               :two-line="false"
                               dense>
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ contact.name_email_company_record_type }}
                      </v-list-item-title>
                      <v-list-item-subtitle v-if="1===2">
                        <EmailDeliverabilityStatus :status="contact.email_deliverability_status"
                                                   :block="false"
                                                   class="d-flex justify-center"
                                                   style="width: 115px;">
                        </EmailDeliverabilityStatus>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </template>
                <template v-slot:no-data>
                  <template v-if="isEmailAddress(search)">
                    <v-list-item v-if="canCreateContact"
                                 @click.prevent="addNewContact"
                                 dense>
                      {{ search }} not found, <span class="ms-2 text-decoration-underline primary--text">add as a new contact</span>.
                    </v-list-item>
                    <v-list-item v-else
                                 dense>
                      {{ search }}" not found.
                    </v-list-item>
                  </template>
                  <v-list-item v-else
                               dense>
                    {{ search }} is not an email address.
                  </v-list-item>
                </template>
              </v-autocomplete>

            </v-tab-item>

            <v-tab-item :value="1"
                        :transition="false"
                        :reverse-transition="false">
              <v-autocomplete v-model="form.list"
                              :disable="form.busy"
                              label="Send to an existing list..."
                              :disabled="form.busy || lists.length === 0"
                              outlined
                              dense
                              autocomplete="off"
                              aria-autocomplete="list"
                              @change="selectEmailList"
                              :clearable="false"
                              @click:clear.prevent="deselectEmailList"
                              :items="lists"
                              item-value="id"
                              item-text="name_and_subscriber_count"
                              return-object>
                <template v-slot:item="{item:list, on, attrs}">
                  <v-list-item v-on="on"
                               v-bind="attrs"
                               two-line
                               dense>
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ list.name }}
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        {{ list.subscriber_count | pluralize('contacts') }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </v-tab-item>
          </v-tabs-items>

          <InvalidEmailAlert v-if="emailVerificationStatus?.is_invalid || didLastMessageBounce"
                             :email="email"
                             :email-verification-status="emailVerificationStatus"
                             :did-last-message-bounce="didLastMessageBounce"
                             class="mb-4 mt-n2">
          </InvalidEmailAlert>

          <v-alert v-if="isRecipientNameCheckingEnabled && recipientsMissingNames.length > 0"
                   type="error"
                   tile
                   dense>
            <div v-if="recipientsMissingNames.length === 1">
              You must enter a salutation and name for {{ recipientsMissingNames[0].email }}.
            </div>
            <div v-else>
              You must enter salutations and names for the following recipients:
              <ul>
                <li v-for="recipient in recipientsMissingNames"
                    :key="recipient.id">
                  {{ recipient.email }}
                </li>
              </ul>
            </div>
          </v-alert>

          <SendEmailRecipientsTable v-if="recipientsTab === 0 && hasRecipients && hasList === false"
                                    ref="recipientsTable"
                                    :contacts="form.recipients"
                                    :loading="false"
                                    @contact-updated="updateRecipient"
                                    @remove-recipient="removeRecipient"
                                    @remove-all-recipients="removeAllRecipients">
          </SendEmailRecipientsTable>

          <div v-if="recipientsTab === 0 && hasManyRecipients && hasList === false"
               class="text-caption pt-2">
            These recipients will be added to a new list.
          </div>
        </v-card-text>
      </v-card>
      <div id="add-recipients-card"></div>

      <div v-if="templateHasLinkTags">
        <v-card class="mb-4">
          <v-card-title>
            <div class="d-flex align-center">
              <v-icon color="primary"
                      class="mr-2">
                fas fa-fw fa-share-square
              </v-icon>
              Share File Links
            </div>
          </v-card-title>
          <v-card-text class="pb-0">
            This template requires you to select files that will appear as links in your email.
          </v-card-text>

          <v-card-text v-if="hasPresentations"
                       class="pb-0">
            <v-list flat
                    tile
                    dense
                    outlined
                    class="py-0 my-0">
              <div class="d-flex justify-space-between align-center px-4 py-2">
                <div class="text-body-2 text-uppercase font-weight-bold">
                  Sharing {{ selectedPresentations.length | pluralize('File') }}
                </div>
                <v-btn v-if="hasManyPresentations"
                       @click.prevent="removeAllPresentations"
                       color="error"
                       elevation="none"
                       small>
                  Remove all
                </v-btn>
              </div>
              <v-divider></v-divider>
              <div style="max-height: 180px; overflow-y: auto;">
                <div v-for="(presentation, index) in selectedPresentations">
                  <v-list-item v-if="presentation && presentation.id"
                               :key="presentation.id"
                               dense>
                    <v-list-item-title>
                      {{ presentation.name }}
                    </v-list-item-title>
                    <v-list-item-action>
                      <v-btn @click.prevent="removePresentation(presentation)"
                             icon
                             x-small
                             color="error"
                             title="Remove this file">
                        <v-icon>
                          fas fa-fw fa-times-circle
                        </v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </v-list-item>
                  <v-divider v-if="index < (form.presentations.length - 1)"></v-divider>
                </div>
              </div>
            </v-list>
          </v-card-text>
          <v-card-actions class="px-4">
            <v-btn @click.prevent="openContentPicker"
                   color="primary">
              <v-icon left>
                fas fa-fw fa-plus
              </v-icon>
              {{ hasPresentations ? 'Add More Files' : 'Select Files to Share' }}
            </v-btn>
          </v-card-actions>
        </v-card>
        <div id="share-files-card"></div>

      </div>

      <div v-if="canWriteCustomMessage">
        <v-card class="mb-4">
          <v-card-title class="d-flex align-center">
            <v-icon color="primary"
                    class="mr-2">
              fas fa-fw fa-edit
            </v-icon>
            <div>
              Write a Custom Message
            </div>
            <div>
              <span class="text-caption text-uppercase ml-2">
                {{ isCustomMessageRequired ? 'Required' : 'Optional' }}
              </span>
            </div>
          </v-card-title>
          <v-card-text class="pb-2">
            <p class="mb-3">
              This will be inserted into the email where the <strong class="EmailTemplate__Token" v-pre>{{ custom_message }}</strong> tag appears.
            </p>

            <v-textarea v-if="1===2"
                        v-model="form.custom_message"
                        clearable
                        autocapitalize="off"
                        spellcheck="false"
                        label="Write a custom message"
                        outlined
                        dense
                        counter
                        maxlength="1000"
                        :required="isCustomMessageRequired"
                        :error-messages="form.errors.get('custom_message')"
                        rows="2">
            </v-textarea>

            <p class="text-caption mb-1">
              The same message will be sent to all recipients. Don't include personal details for a specific recipient.
            </p>

            <p class="text-caption mb-1">
              Plain text only - links, bold, italics, and other formatting are not supported.
            </p>

            <p class="text-caption mb-0">
              Follow your company's compliance guidelines for what content is allowed.
            </p>
          </v-card-text>
        </v-card>
        <div id="write-custom-message-card"></div>
      </div>

      <div v-if="template && customTags.length > 0">
        <v-card class="mb-4">
          <v-card-title>
            <v-icon color="primary"
                    class="mr-2">
              fas fa-fw fa-puzzle-piece
            </v-icon>
            Personalize Your Message
          </v-card-title>
          <v-card-text class="pb-0">
            <p>
              Your selections will replace the blue <strong class="EmailTemplate__Token" v-pre>{{customTags}}</strong> in the message.
            </p>
            <div v-for="(customTag, index) in customTags"
                 :key="customTag.id"
                 class="mb-4">
              <v-text-field v-if="customTag.is_date"
                            v-model="form.custom_tags[index]"
                            type="date"
                            :label="customTag.is_required ? `${customTag.name} (Required)` : `${customTag.name} (Optional)`"
                            :placeholder="customTag.is_required ? 'Required' : 'Optional'"
                            :items="customTag.options"
                            :required="customTag.is_required"
                            :rules="customTag.is_required ? [rules.required] : []"
                            :hint="customTag.instructions"
                            :persistent-hint="!!customTag.instructions"
                            outlined
                            dense
                            :min="today"
                            :class="!!customTag.instructions ? 'mb-4' : ''">
              </v-text-field>
              <v-select v-else-if="customTag.is_time"
                        v-model="form.custom_tags[index]"
                        :label="customTag.is_required ? `${customTag.name} (Required)` : `${customTag.name} (Optional)`"
                        :placeholder="customTag.is_required ? 'Required' : 'Optional'"
                        :items="timeOptions"
                        item-value="value"
                        item-text="label"
                        :rules="customTag.is_required ? [rules.required] : []"
                        :hint="customTag.instructions"
                        :persistent-hint="!!customTag.instructions"
                        outlined
                        dense>
              </v-select>

              <v-select v-else-if="customTag.has_options"
                        v-model="form.custom_tags[index]"
                        :label="customTag.is_required ? `${customTag.name} (Required)` : `${customTag.name} (Optional)`"
                        :placeholder="customTag.is_required ? 'Required' : 'Optional'"
                        :items="customTag.options"
                        :item-text="customTag.is_list ? 'value' : 'name'"
                        item-value="value"
                        :required="customTag.is_required"
                        :rules="customTag.is_required ? [rules.required] : []"
                        :hint="customTag.instructions"
                        :persistent-hint="!!customTag.instructions"
                        outlined
                        dense
                        :class="!!customTag.instructions ? 'mb-4' : ''">
              </v-select>

              <v-text-field v-else-if="customTag.is_text"
                            v-model="form.custom_tags[index]"
                            :label="customTag.is_required ? `${customTag.name} (Required)` : `${customTag.name} (Optional)`"
                            :placeholder="customTag.is_required ? 'Required' : 'Optional'"
                            :required="customTag.is_required"
                            :rules="customTag.is_required ? [rules.required] : []"
                            :hint="customTag.instructions"
                            :persistent-hint="!!customTag.instructions"
                            outlined
                            dense
                            :class="!!customTag.instructions ? 'mb-4' : ''">
              </v-text-field>
            </div>
          </v-card-text>
        </v-card>
        <div id="personalize-message-card"></div>
      </div>

      <v-card class="mb-4"
              tile>
        <v-card-title>
          <v-icon color="primary"
                  class="mr-2">
            fas fa-fw fa-pen-square
          </v-icon>
          Message Content
        </v-card-title>
        <v-card-text class="pb-2">
          <v-select v-if="subjectLines.length > 1"
                    v-model="form.subject"
                    :items="subjectLines"
                    item-text="subject"
                    item-value="subject"
                    label="Subject"
                    outlined
                    dense
                    required>
          </v-select>
          <v-text-field v-else
                        v-model="form.subject"
                        label="Subject"
                        outlined
                        dense
                        autocomplete="off"
                        aria-autocomplete="list"
                        required
                        :disabled="isTemplateSubjectLocked"
                        :readonly="isTemplateSubjectLocked"
                        :hint="isTemplateSubjectLocked ? 'You can\'t edit this template\'s subject line.' : ''"
                        :persistent-hint="isTemplateSubjectLocked"
                        :error-messages="form.errors.get('subject')">
          </v-text-field>

          <v-alert v-if="isContentEditable === false"
                   type="info"
                   dense
                   tile
                   class="mb-0">
            You're not allowed to edit this template.
          </v-alert>

          <div v-if="useTinyMCE">
            <div id="tinyContainer"
                 :class="{'has-error': form.errors.has('html_body')}"
                 :style="`min-height: ${tinyMinHeight}px`">
              <div ref="tinyTextarea"
                   id="tinyEditor"
                   :style="`min-height: ${tinyMinHeight}px`">
              </div>
            </div>

            <div class="text-caption mt-2">
              <div>
                The <span class="EmailTemplate__Token">blue tokens</span> are used to personalize the email for each recipient. Do not edit or remove them.
              </div>
            </div>
          </div>
          <div v-else-if="dataURI"
               style="width: 100%; height: 300px;">
            <iframe :src="dataURI"
                    style="border: 1px solid #aaaaaa; overflow-y: auto;">
            </iframe>
          </div>
        </v-card-text>

        <ShareTemplateErrors :form="form"
                             :template="template">
        </ShareTemplateErrors>
      </v-card>
      <div id="message-content-card"></div>

      <div v-if="isFormValid">
        <div class="text-caption px-4 mb-4">
          <a href="#pageTitle" @click.prevent="scrollToTop">Scroll to the top</a> to review and send this message to {{ totalRecipients | pluralize('recipient', true) }}.
        </div>
      </div>

    </div>

    <EmailTemplateIntructionsDialog v-if="showInstructionsDialog"
                                    :dialog="showInstructionsDialog"
                                    :template="template"
                                    @close="closeInstructions">
    </EmailTemplateIntructionsDialog>

    <v-dialog v-if="confirmDialog"
              v-model="confirmDialog"
              persistent
              max-width="660px">
      <v-card>
        <v-card-title class="pb-4">
          <div class="text-h6">
            {{ confirmQuestion }}
          </div>
        </v-card-title>
        <v-card-text class="">
          <SendFailureNotificationsCheckbox :form="form">
          </SendFailureNotificationsCheckbox>
        </v-card-text>
        <v-card-actions class="px-4">
          <v-btn color="primary"
                 @click.prevent="confirmSend">
            <template v-if="isScheduled">
              Yes, schedule it
            </template>
            <template v-else>
              Yes, send it now
            </template>
          </v-btn>
          <v-btn @click.prevent="cancelSend"
                 text>
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ContentPickerDialog v-if="showContentPicker"
                         :dialog="showContentPicker"
                         :form="form"
                         @close="closeContentPicker"
                         @insert="closeContentPicker">
    </ContentPickerDialog>

    <div id="bottom"></div>
  </v-container>
</template>
<script>
import {Form} from 'vform';
import tinymce from 'tinymce';
import shareTemplateService from '../../../../ml/services/shareTemplateService';
import {clone, debounce, find, findIndex, isArray, isInteger, isObject, isString, orderBy} from 'lodash';
import validationService from '../../services/validationService';
import userContactService from '../../services/userContactService';
import {mapGetters} from 'vuex';
import emailVerificationService from '../../services/emailVerificationService';
import InvalidEmailAlert from './components/InvalidEmailAlert.vue';
import tinymceService from '../../../../ml/services/tinymceService';
import DOMPurify from 'dompurify';
import htmlService from '../../services/htmlService';
import moment from 'moment-timezone';
import timezoneService from '../../services/timezoneService';
import sharedLinkService from '../../../../ml/services/sharedLinkService';
import presentationService from '../../../../ml/services/presentationService';
import PageTitle from '../../components/PageTitle.vue';
import ContentPickerDialog from './wizard/components/ContentPickerDialog.vue';
import EmailTemplateIntructionsDialog from './components/EmailTemplateIntructionsDialog.vue';
import EmailTemplateInstructionsAlert from './components/EmailTemplateInstructionsAlert.vue';
import RecentTemplatesList from './components/RecentTemplatesList.vue';
import mailcoachService from '../../services/mailcoachService';
import pluralize from 'pluralize';
import scrollingService from '../../services/scrollingService';
import SendEmailRecipientsTable from './components/SendEmailRecipientsTable.vue';
import EmailDeliverabilityStatus from '../neverbounce/components/EmailDeliverabilityStatus.vue';
import formValidation from '../../plugins/formValidation';
import ShareTemplateErrors from '../share_templates/components/ShareTemplateErrors.vue';
import SendFailureNotificationsCheckbox from './components/SendFailureNotificationsCheckbox.vue';
import ErrorAlert from '../../components/ErrorAlert.vue';
import browserlessService from '../../services/browserlessService';

const DAYS_IN_FUTURE = 90; // MLD-7115

export default {
  mixins: [formValidation],
  components: {ErrorAlert, SendFailureNotificationsCheckbox, ShareTemplateErrors, EmailDeliverabilityStatus, SendEmailRecipientsTable, EmailTemplateInstructionsAlert, EmailTemplateIntructionsDialog, ContentPickerDialog, PageTitle, InvalidEmailAlert, RecentTemplatesList},
  data() {
    return {
      useTinyMCE: false,
      editor: null,
      editorInitialized: false, // MLD-7930
      htmlContent: '', // MLD-7930 Direct content sync with TinyMCE
      form: new Form({
        send_failure_notifications: false,
        list: null,
        contact: null,
        recipients: [],
        subject: null,
        html_body: null,
        template_id: null,
        presentations: [],
        scheduled_at: null,
        scheduled_date: null,
        scheduled_time: null,

        // @see https://attacomsian.com/blog/javascript-current-timezone
        scheduled_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        custom_message: null, // MLD-3998
        custom_tags: [], // MLD-6454
      }),
      addingContact: false,
      listsLoading: false,
      lists: [],
      subscribers: [],
      contactsLoading: false,
      contacts: [],
      template: null,
      templatesLoading: false,
      searchTemplates: '', // MLD-6870
      templates: [],
      search: '',
      searchError: false,
      debouncedSearchContacts: null, // MLD-7293 Add the debounced function as a data property (initially null)
      email: null,
      emailVerificationStatus: null, // MLD-6016
      didLastMessageBounce: false, // MLD-6336

      customTags: [], // MLD-6454

      recipientsTab: 0, // MLD-7231

      mounted: false,
      links: [],
      spaceIDs: [], // MLD-4908
      notificationLevels: sharedLinkService.getNotificationLevels(),
      presentation: null,
      tinyError: false, // MLD-2759
      previewData: null,
      previewHTML: null, // MLD-2759
      showContentPicker: false,
      showSpacePicker: false, // MLD-4908
      showCannotShareDialog: false,
      showInstructionsDialog: false, // MLD-3031
      scheduleMinutesInterval: 5,
      isScheduled: false,
      scheduleDialog: false, // MLD-6106
      scheduleError: null, // MLD-6106
      confirmDialog: false,
      confirmed: false,
      subjectLines: [], // MLD-6517
      timeOptions: [ // MLD-7076
        {label: '6:00 AM', value: '06:00'},
        {label: '6:30 AM', value: '06:30'},
        {label: '7:00 AM', value: '07:00'},
        {label: '7:30 AM', value: '07:30'},
        {label: '8:00 AM', value: '08:00'},
        {label: '8:30 AM', value: '08:30'},
        {label: '9:00 AM', value: '09:00'},
        {label: '9:30 AM', value: '09:30'},
        {label: '10:00 AM', value: '10:00'},
        {label: '10:30 AM', value: '10:30'},
        {label: '11:00 AM', value: '11:00'},
        {label: '11:30 AM', value: '11:30'},
        {label: '12:00 PM', value: '12:00'},
        {label: '12:30 PM', value: '12:30'},
        {label: '1:00 PM', value: '13:00'},
        {label: '1:30 PM', value: '13:30'},
        {label: '2:00 PM', value: '14:00'},
        {label: '2:30 PM', value: '14:30'},
        {label: '3:00 PM', value: '15:00'},
        {label: '3:30 PM', value: '15:30'},
        {label: '4:00 PM', value: '16:00'},
        {label: '4:30 PM', value: '16:30'},
        {label: '5:00 PM', value: '17:00'},
        {label: '5:30 PM', value: '17:30'},
        {label: '6:00 PM', value: '18:00'},
        {label: '6:30 PM', value: '18:30'},
        {label: '7:00 PM', value: '19:00'},
        {label: '7:30 PM', value: '19:30'},
        {label: '8:00 PM', value: '20:00'},
        {label: '8:30 PM', value: '20:30'},
        {label: '9:00 PM', value: '21:00'},
        {label: '9:30 PM', value: '21:30'},
        {label: '10:00 PM', value: '22:00'},
      ],
      // MLD-7224
      previewDialog: false,
      preview_error: null,
      preview_contact: null,
      preview_content: null,
      preview_from_address: null,
      preview_from_name: null,
      preview_subject: null,
      previewImage: null, // MLD-7932
      previewImageWidth: 600,
      // MLD-7502
      previewContacts: [],
    }
  },
  computed: {
    ...mapGetters([
      'canCreateContact', // MLD-3127
      'canEmailSelf', // MLD-5505
      'canScheduleEmails', // MLD-6106
      'canUseSnippets',
      'canVerifyEmails', // MLD-6016
      'crmLabel',
      'isAdmin',
      'isMember',
      'team',
      'user',
    ]),
    ...mapGetters('content', [
      'activeSnippets', // MLD-3900
      'mustExitProperly',
      'presentations',
    ]),
    previewImageURI() { // MLD-7932
      if (isObject(this.previewImage) && isString(this.previewImage.image)) {
        return `data:image/png;base64,${this.previewImage.image}`;
      }
      return null;
    },
    today() { // MLD-7076
      return moment().format('YYYY-MM-DD');
    },
    tableHeight() {
      if (this.filteredTemplates.length > 6) {
        return `260px`;
      }
      return null;
    },
    headers() {
      return [
        {
          text: 'Name',
          value: 'name',
          sortable: true,
          //cellClass: 'text-nowrap',
        },
        {
          text: 'Subject',
          value: 'subject',
          sortable: true,
          //cellClass: 'text-nowrap',
        },
        {
          text: 'Brand',
          value: 'brand.name',
          sortable: true,
          cellClass: 'text-nowrap',
        },
        {
          text: 'Expires',
          value: 'expires_at',
          sortable: true,
          cellClass: 'text-nowrap',
        }
      ]
    },
    customTagsAnswered() { // MLD-6454
      if (this.customTags.length === 0) {
        return true;
      }
      const requiredCustomTags = this.form.custom_tags.filter(tag => tag.is_required);
      let answered = requiredCustomTags.reduce((accumulator, tagValue) => {
        if (tagValue) {
          return accumulator + 1;
        }
        return accumulator;
      }, 0);
      return answered === requiredCustomTags.length;
    },
    contactID() { // MLD-6641
      if (this.$route.query.contact_id) {
        try {
          const id = parseInt(this.$route.query.contact_id);
          if (isInteger(id)) {
            return id;
          }
        } catch (e) {

        }
      }
      return null;
    },
    listID() {
      if (this.$route.query.list_id) {
        try {
          const id = parseInt(this.$route.query.list_id);
          if (isInteger(id)) {
            return id;
          }
        } catch (e) {

        }
      }
      return null;
    },
    presentationID() {
      if (this.$route.query.presentationID) {
        try {
          const id = parseInt(this.$route.query.presentationID);
          if (isInteger(id)) {
            return id;
          }
        } catch (e) {

        }
      } else if (this.$route.query.presentation_id) {
        try {
          return parseInt(this.$route.query.presentation_id);
        } catch (e) {

        }
      }
      return null;
    },
    templateID() {
      if (this.$route.query.template_id) {
        try {
          const id = parseInt(this.$route.query.template_id);
          if (isInteger(id)) {
            return id;
          }
        } catch (e) {
        }
      }
      return null;
    },
    crmRelatedObject() { // MLD-6845
      return this.$route.query.crm_related_object ?? null;
    },
    crmRelatedObjectID() { // MLD-6845
      return this.$route.query.crm_related_object_id ?? null;
    },
    filteredTemplates() {
      const templates = this.templates.filter(template => { // MLD-2290
        return template.is_active
          && template.can_be_selected
          && (template.is_app || template.is_web);
      });

      return orderBy(templates, ['is_featured', 'slug'], ['desc', 'asc']);
    },
    filteredContacts() {
      return this.contacts;
    },
    recipientEmails() {
      return this.form.recipients.map(c => c.email);
    },
    hasPreviewContacts() {
      if (isArray(this.previewContacts)) {
        return this.previewContacts.length > 0;
      }
      return false;
    },
    hasList() { // MLD-7502
      return this.form.list !== null;
    },
    totalRecipients() { // MLD-7502
      if (this.hasList) {
        return this.form.list?.subscriber_count
      }
      return this.form.recipients.length;
    },
    hasRecipients() {
      return this.form.recipients.length > 0;
    },
    hasManyRecipients() {
      return this.form.recipients.length > 1;
    },
    isRecipientNameCheckingEnabled() { // MLD-7219 Temporary for Allergan.
      return this.team?.id !== 194;
    },
    recipientsMissingNames() { // MLD-7022
      if (this.hasRecipients === false) {
        return [];
      }
      return this.form.recipients.filter(r => !r.name);
    },
    hasPresentations() {
      return this.form.presentations.length > 0;
    },
    hasManyPresentations() {
      return this.form.presentations.length > 1;
    },
    selectedPresentations() {
      const presentations = [];
      this.form.presentations.forEach(presentationID => {
        const presentation = find(this.presentations, p => p.id === presentationID);
        if (presentation) {
          presentations.push(presentation);
        }
      })
      return presentations;
    },
    isSearchEmpty() {
      if (isString(this.search)) {
        return this.search.trim().length === 0;
      }
      return true;
    },
    isApp() {
      return this.isElectron || this.isIOS;
    },
    isElectron() { // MLD-3108
      return this.$route
        && this.$route.query
        && this.$route.query.source
        && this.$route.query.source === 'electron';
    },
    isFormValid() {
      if (!this.template) {
        return false;
      }
      if (!this.htmlContent) {
        return false;
      }
      if (this.hasRecipients === false && this.hasList === false) {
        return false;
      }
      if (this.templateHasLinkTags && this.hasPresentations === false) {
        return false;
      }
      if (this.isCustomMessageRequired && !this.form.custom_message) {
        return false;
      }
      if (this.hasRecipients
        && this.isRecipientNameCheckingEnabled // MLD-7219
        && this.recipientsMissingNames.length > 0) { // MLD-7022
        return false;
      }
      return true;
    },
    isPreviewValid() { // MLD-7224
      return isObject(this.preview_contact)
        && isString(this.preview_subject)
        && isString(this.preview_content)
        && isString(this.preview_from_address)
        && isString(this.preview_from_name);
    },
    templateHasLinkTags() { // MLD-3143
      return this.template
        && (this.template.has_share_link_tag || this.template.has_shared_links_tag);
    },
    canWriteCustomMessage() { // MLD-6775
      return this.template
        && this.template.is_custom_message_allowed
        && this.template.has_custom_message_tag;
    },
    isCustomMessageAllowed() { // MLD-6775
      return this.template
        && this.template.is_custom_message_allowed;
    },
    isCustomMessageRequired() { // MLD-6775
      return this.isCustomMessageAllowed
        && this.template.is_custom_message_required;
    },
    isIOS() {
      return this.$route
        && this.$route.query
        && this.$route.query.source
        && this.$route.query.source === 'ios';
    },
    tags() { // MLD-2726
      const tags = [
        {tag: 'user_signature', name: 'My signature'},
      ];
      if (this.contact) {
        tags.push({tag: 'recipient.name', name: `Contact name: ${this.contact.name}`})
        if (this.contact.greeting) {
          tags.push({tag: 'recipient.greeting', name: `Contact greeting: ${this.contact.greeting}`});
        }
        if (this.contact.salutation) {
          tags.push({tag: 'recipient.salutation', name: `Contact salutation: ${this.contact.salutation}`});
        }
        tags.push({tag: 'recipient.first_name', name: `Contact first name: ${this.contact.first_name}`});
        tags.push({tag: 'recipient.last_name', name: `Contact last name: ${this.contact.last_name}`});
      }
      if (this.presentation) {
        tags.push({tag: 'presentation_name', name: `File name: ${DOMPurify.sanitize(this.presentation.name)}`});
      }
      return tags;
    },
    isContentEditable() { // MLD-3769
      if (!this.template) {
        return true;
      }
      return this.template
        && this.template.is_editable;
    },
    contextMenu() {
      let items = [
        'context_menu_ml_insert_content',
        'context_menu_ml_insert_spaces',
      ]
      if (this.canShareLinks === false) {
        items = items.filter(i => i !== 'context_menu_ml_insert_content');
      }
      if (this.canShareSpaces === false) {
        items = items.filter(i => i !== 'context_menu_ml_insert_spaces');
      }
      return items.join(' ');
    },
    quickbarsSelectionToolbar() { // MLD-4908
      let items = [
        'button_ml_insert_content',
        'button_ml_insert_spaces'
      ]
      if (this.canShareLinks === false) {
        items = items.filter(i => i !== 'button_ml_insert_content');
      }
      if (this.canShareSpaces === false) {
        items = items.filter(i => i !== 'button_ml_insert_spaces');
      }
      return items.join(' | ');
    },
    toolbar() { // MLD-4908
      let items = [
        'toggle_button_ml_fullscreen',
        'button_ml_insert_content',
        'button_ml_insert_spaces',
        'menu_button_ml_snippet',
        'menu_button_ml_fragments',
        'undo redo',
        'bold italic underline removeformat',
        'numlist bullist',
        'link',
      ];
      if (this.canShareLinks === false) {
        items = items.filter(i => i !== 'button_ml_insert_content');
      }
      if (this.canShareSpaces === false) {
        items = items.filter(i => i !== 'button_ml_insert_spaces');
      }
      return items.join(' | ');
    },
    canShareSpaces() { // MLD-4908
      if (this.template) {
        return this.canUserShareSpaces // MLD-5030
          && this.isContentEditable
          && this.template.can_share_links;
      }
      return this.canUserShareSpaces;
    },
    confirmQuestion() {
      return this.isScheduled
        ? `Are you ready to send this email to ${pluralize('recipient', this.totalRecipients, true)} at ${this.formattedScheduledAt}?`
        : `Are you ready to send this email to ${pluralize('recipient', this.totalRecipients, true)} now?`;
    },
    canShareLinks() {
      if (!this.template) {
        return true;
      }
      return this.isContentEditable // MLD-3769
        && this.template.can_share_links;
    },
    isSubjectEditable() {
      if (!this.template) {
        return true;
      }
      return this.template
        && this.template.is_subject_editable;
    },
    isTemplateSubjectLocked() { // MLD-2695
      return this.template
        && !this.template.is_subject_editable;
    },
    canInsertSpaceLinks() { // MLD-4908
      if (!this.useTinyMCE) {
        return false;
      }
      return isArray(this.form.spaces)
        && this.form.spaces.length > 0;
    },
    canInsertPresentationLinks() {
      if (!this.useTinyMCE) {
        return false;
      }
      return isArray(this.form.presentations)
        && this.form.presentations.length > 0;
    },
    dataURI() { // MLD-2473
      if (this.template?.is_unlayer) { // MLD-6455
        let html = `<!DOCTYPE HTML PUBLIC "-//W3C//DTD XHTML 1.0 Transitional //EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd"><html xmlns="http://www.w3.org/1999/xhtml" lang="en"><head>${this.template?.html_head ?? ''}${this.template?.css ? `\n<style>\n${this.template.css}\n</style>\n` : ''}</head><body>${this.htmlContent}</body></html>`
        if (this.htmlContent?.startsWith('<body')) {
          html = `<!DOCTYPE HTML PUBLIC "-//W3C//DTD XHTML 1.0 Transitional //EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd"><html xmlns="http://www.w3.org/1999/xhtml" lang="en"><head>${this.template?.html_head ?? ''}${this.template?.css ? `\n<style>\n${this.template.css}\n</style>\n` : ''}</head>${this.htmlContent}</html>`
        }
        return htmlService.dataURI(html);
      } else if (this.htmlContent) {
        return htmlService.dataURI(this.htmlContent);
      }
      return null;
    },
    fromName() {
      if (this.previewData && this.previewData.from_name && this.previewData.from_address) {
        return `${this.previewData.from_name} <${this.previewData.from_address}>`
      }
      return this.user.name;
    },
    previewDataURI() {
      if (this.preview_content) {
        return htmlService.dataURI(this.preview_content);
      }
      return null;
    },
    templateName() {
      if (this.template) {
        return this.template.name;
      }
      return 'None';
    },
    /*useTinyMCE() { // MLD-2473
      if (this.template) { // TinyMCE and Unlayer templates can use TinyMCE
        if (false === this.isContentEditable) { // MLD-3769 unless the template is not editable.
          console.log('TinyMCE useTinyMCE isContentEditable is false');
          return false;
        }
        console.log('TinyMCE useTinyMCE isContentEditable is true');
        return this.template.is_tinymce; // || this.template.is_unlayer;
      }
      console.log('TinyMCE useTinyMCE template is null');
      return true;
    },*/
    tinyMinHeight() {
      const defaultHeight = 100;
      switch (this.$vuetify.breakpoint.name) {
        case 'xs':
          return 220
        case 'sm':
          return defaultHeight
        case 'md':
          return defaultHeight
        case 'lg':
          return defaultHeight + 100;
        case 'xl':
          return defaultHeight + 100;
        default:
          return defaultHeight;
      }
    },
    tinyConfig() {
      let tableToolbar = '';

      const config = {
        content_css: `/v2/css/tinymce_editor.css?v=${new Date().getTime()}`, // MLD-2694
        selector: '#tinyEditor',
        resize: true,
        min_height: this.tinyMinHeight,

        //quickbars_insert_toolbar: 'ml_quick_insert', // MLD-2693 @see https://www.tiny.cloud/docs/plugins/opensource/quickbars/#exampledisablingthequickinsertcontexttoolbar
        quickbars_insert_toolbar: false,
        //quickbars_selection_toolbar: 'button_ml_insert_content | bold italic | quicklink', // MLD-2693 @see https://www.tiny.cloud/docs/plugins/opensource/quickbars/#quickbars_selection_toolbar
        quickbars_selection_toolbar: this.quickbarsSelectionToolbar,
        plugins: 'mobilelockereditor anchor charmap code codesample emoticons fullscreen help hr image link lists media nonbreaking noneditable paste preview quickbars searchreplace table tabfocus template textpattern visualblocks',
        toolbar: this.toolbar,
        style_formats_autohide: true,
        table_toolbar: tableToolbar, // @see https://www.tiny.cloud/docs/plugins/opensource/table/#table_toolbar
        visual: false, // @see https://www.tiny.cloud/docs/configure/content-appearance/#visual
        contextmenu: this.contextMenu,
        noneditable_regexp: tinymceService.getNonEditableRegularExpressions(),
        init_instance_callback: (editor) => {
          const elm = editor.dom.select('#tinymce')
          editor.dom.addClass(elm, 'EditMode');
        },
        // Remove the setup function from here since we handle it manually
      }
      return tinymceService.getConfig(config);
    },
    returnPrompt() { // MLD-2947
      return `You cannot exit that way.`;
    },
    scheduleDates() { // MLD-6106
      const dates = [];
      let startDate = moment().startOf('day');
      const endDate = moment().startOf('day').add(DAYS_IN_FUTURE, 'days');

      while (startDate <= endDate) {
        dates.push({
          id: startDate.format('YYYY-MM-DD'),
          text: startDate.format('dddd, MMMM D, YYYY')
        });
        startDate = startDate.add(1, 'day');
      }

      return dates;
    },
    scheduleTimes() { // MLD-6106 @see https://chatgpt.com/c/2f17c818-af3a-4bc9-9284-2c88f91bd9d7
      const times = [];
      if (this.form.scheduled_date) {
        const selectedDate = moment(this.form.scheduled_date, 'YYYY-MM-DD');
        const currentDate = moment().startOf('day');
        const lastDate = moment().add(DAYS_IN_FUTURE, 'day');
        let startTime = moment('06:00', 'HH:mm');
        let endTime = moment('23:59', 'HH:mm');

        if (selectedDate.isSame(currentDate, 'day')) {
          startTime = moment();
          startTime = startTime.minutes(Math.ceil(startTime.minutes() / this.scheduleMinutesInterval) * this.scheduleMinutesInterval);
        }
        if (selectedDate.isSame(lastDate, 'day')) {
          endTime = moment(lastDate.format('HH:mm'), 'HH:mm');
        }

        while (startTime <= endTime) {
          times.push({
            id: startTime.format('HH:mm'),
            text: startTime.format('h:mm A')
          });
          startTime = startTime.add(this.scheduleMinutesInterval, 'minutes');
        }
      }

      return times;
    },
    scheduledTimeZone() {
      return timezoneService.getFriendlyTimezone()
    },
    computeScheduledAt() { // MLD-6106
      if (this.form.scheduled_date && this.form.scheduled_time) {
        return moment(`${this.form.scheduled_date} ${this.form.scheduled_time}`, 'YYYY-MM-DD HH:mm').utc();
      }
      return null;
    },
    isScheduleFormValid() { // MLD-6106
      if (!this.computeScheduledAt) {
        return false;
      }
      return this.computeScheduledAt.isAfter(moment())
        && this.computeScheduledAt.isBefore(moment().add(DAYS_IN_FUTURE, 'days'))
    },
    mustUseTemplate() { // MLD-6798
      return this.presentation
        && this.presentation.share_template_id
        && this.presentation.is_template_required;
    },
  },
  beforeRouteLeave(to, from, next) { // MLD-2947
    if (this.mustExitProperly) {
      const leave = confirm('Are you sure you want to leave without sending the email?');
      //alert(this.returnPrompt);
      return next(leave);
    }
    next();
  },
  beforeDestroy() {
    this.destroyTinyMCE();
    window.removeEventListener('beforeunload', this.promptBeforeUnloading, {capture: true});
  },
  async created() {
    // MLD-7293 Initialize the debounced version of searchContacts
    this.debouncedSearchContacts = debounce(this.searchContacts, 300); // 300ms
  },
  async mounted() {
    await this.fetchData();
  },
  watch: {
    search(val) {
      // MLD-7293 Call the debounced version instead of the original method
      this.debouncedSearchContacts(val);
    },
    htmlContent(newValue) {
      if (this.editor && this.editorInitialized) {
        const currentContent = this.editor.getContent();
        // Only update if content is different and editor isn't being actively edited
        if (currentContent !== newValue && !this.editor.isDirty()) {
          this.editor.setContent(newValue || '');
          // console.log('TinyMCE content synced with htmlContent');
        } else {
          // console.log('TinyMCE content is already in sync with htmlContent');
        }
      }
    },
    // MLD-7930 Watch for useTinyMCE changes to handle DOM recreation
    /*useTinyMCE(newVal) {
      if (newVal) {
        this.ensureTinyMCEInitialized();
      } else {
        this.destroyTinyMCE();
      }
    },*/
    // MLD-7930 Watch for tab changes - removed because Message Content card doesn't re-render
    // recipientsTab(newTab) {
    //   this.ensureTinyMCEInitialized();
    // }
  },
  methods: {
    async initializeTinyMCE() { // MLD-7930
      // console.log('TinyMCE initializeTinyMCE()');

      // Skip if already initialized
      if (this.editorInitialized && this.editor) {
        // console.log('TinyMCE already initialized');
        return;
      }

      // Destroy the existing instance if it exists
      this.destroyTinyMCE();

      // Wait for DOM to be ready
      await this.$nextTick();

      // Check if we should initialize TinyMCE
      if (!this.useTinyMCE) {
        // console.log('TinyMCE not enabled');
        return;
      }

      if (!this.$refs.tinyTextarea) {
        // console.warn('TinyMCE textarea ref not found, retrying...');
        // Retry after a short delay
        setTimeout(() => {
          this.initializeTinyMCE();
        }, 100);
        return;
      }

      try {
        const config = {
          ...this.tinyConfig,
          target: this.$refs.tinyTextarea, // Target the div directly
          inline: false, // Ensure it's not inline mode
          setup: (editor) => {
            // console.log('TinyMCE setup()');

            // Call your existing setup logic
            this.setupTinyMCEButtons(editor);

            // Handle content changes for two-way binding
            editor.on('input change keyup', () => {
              // console.log('TinyMCE input change keyup');
              const content = editor.getContent();
              if (content !== this.htmlContent) {
                this.htmlContent = content;
              }
            });

            // Handle initialization complete
            editor.on('init', () => {
              // console.log('TinyMCE init()');
              // Set initial content from htmlContent
              const content = this.htmlContent || '';
              editor.setContent(content);
              //this.editor = editor;
              // console.log('TinyMCE initialized and content set');
            });
          }
        };

        const editors = await tinymce.init(config);
        if (editors && editors.length > 0) {
          // console.log('TinyMCE editor created');
          this.editor = editors[0];
          this.editorInitialized = true;

          // Ensure content is set after editor creation
          /*if (this.htmlContent && this.editor) {
            this.editor.setContent(this.htmlContent);
            console.log('TinyMCE content set after creation');
          }*/
        }
      } catch (error) {
        console.error('Failed to initialize TinyMCE:', error);
      }
    },

    setupTinyMCEButtons(editor) {
      // console.log('TinyMCE setupTinyMCEButtons()');
      // Move all your button setup logic here
      editor.ui.registry.addToggleButton('toggle_button_ml_fullscreen', {
        text: 'Full screen',
        onAction: (api) => {
          editor.execCommand('mceFullScreen');
          api.setActive(!api.isActive());
        },
      });

      if (this.isContentEditable && this.canUseSnippets) {
        editor.ui.registry.addMenuButton('menu_button_ml_snippet', {
          text: 'Insert snippet',
          fetch: (callback) => {
            let items = this.activeSnippets.map(snippet => {
              return {
                type: 'menuitem',
                text: snippet.name,
                onAction: () => {
                  const data = {
                    presentation_id: this.presentationID,
                    template_id: this.template ? this.template.id : null,
                    user_contact_id: this.contact ? this.contact.id : null,
                  }
                  shareTemplateService.renderText(snippet.content, data)
                    .then(data => {
                      editor.insertContent(data.output);
                    })
                    .catch(err => {
                      this.$toast.error(`There was a problem inserting that content`);
                    })
                }
              }
            });
            callback(items);
          }
        });
      }
    },

    // MLD-7930 Helper method to ensure TinyMCE is initialized
    ensureTinyMCEInitialized() {
      // console.log('ensureTinyMCEInitialized called - useTinyMCE:', this.useTinyMCE, 'editorInitialized:', this.editorInitialized);
      if (this.useTinyMCE) {
        if (!this.editorInitialized) {
          // console.log('TinyMCE not initialized, scheduling initialization');
          this.$nextTick(() => {
            setTimeout(() => {
              this.initializeTinyMCE();
            }, 50); // Small delay to ensure DOM is fully updated
          });
        } else if (this.editor && this.htmlContent) {
          // If already initialized but content might be missing, update it
          const currentContent = this.editor.getContent();
          // console.log('TinyMCE already initialized - current content length:', currentContent.length, 'htmlContent length:', this.htmlContent.length);
          if (currentContent !== this.htmlContent && this.htmlContent) {
            this.editor.setContent(this.htmlContent);
            // console.log('TinyMCE content updated after reinitialization');
          } else {
            // console.log('TinyMCE content is already in sync, no update needed');
          }
        } else {
          // console.log('TinyMCE initialized but editor or htmlContent missing - editor:', !!this.editor, 'htmlContent:', !!this.htmlContent);
        }
      } else {
        // console.log('useTinyMCE is false, skipping initialization');
      }
    },

    destroyTinyMCE() { // MLD-7930
      if (this.editor) {
        // console.log('TinyMCE destroyTinyMCE()');
        try {
          // Save content before destroying
          /*const content = this.editor.getContent();
          if (content !== this.htmlContent) {
            this.htmlContent = content;
          }*/

          this.editor.destroy();
          this.editor = null;
          this.editorInitialized = false;
          // console.log('TinyMCE destroyed');
        } catch (error) {
          console.error('Error destroying TinyMCE:', error);
          this.editor = null;
          this.editorInitialized = false;
        }
      }
    },

    toggleFullScreen() {
      if (this.editor) {
        this.editor.execCommand('mceFullScreen');
      }
    },
    openSpacePicker() { // MLD-4908
      this.form.spaces = [];
      this.showSpacePicker = true;
    },
    closeSpacePicker() { // MLD-4908
      this.showSpacePicker = false;
    },
    openContentPicker() {
      //this.form.presentations = [];
      this.showContentPicker = true;
    },
    closeContentPicker() {
      this.showContentPicker = false;
      //scrollingService.scrollToSelector('#bottom');
    },
    isString(val) {
      return isString(val);
    },
    isEmailAddress(val) {
      return validationService.isEmailValid(val);
    },
    isEmailInContacts(search) { // MLD-6016
      if (isString(search)) {
        const email = search.toLowerCase().trim();
        if (email) {
          const emails = this.filteredContacts.map(c => c.email);
          return emails.includes(email);
        }
      }
      return false;
    },
    fetchSnippets() {
      this.$store.dispatch('content/fetchSnippets', {user_id: this.user.id})
        .then()
        .catch(() => {
        })
    },
    fetchSpaces() {
      this.$store.dispatch('content/fetchSpaces')
        .then()
        .catch(() => {
        })
    },
    findTemplate(templateID) {
      if (isInteger(templateID)) {
        return find(this.templates, t => t.id === templateID);
      }
      return null;
    },
    findList(listID) {
      if (isInteger(listID)) {
        return find(this.lists, l => l.id === listID);
      }
      return null;
    },
    fetchLists() {
      const params = {
        min_subscriber_count: 2,
      };
      return mailcoachService.getListsForUser(this.user.id, params)
        .then(data => {
          this.lists = orderBy(data, ['updated_at', 'slug'], ['desc', 'asc']);
          const list = this.findList(this.listID);
          if (list) {
            this.form.list = list;
            this.recipientsTab = 1; // MLD-7502
            this.selectEmailList();
          }
        })
    },
    async deselectEmailList() { // MLD-7502
      // console.log('TinyMCE deselectEmailList()', this.form.list);
      if (this.form.list) {
        // console.log('TinyMCE deselectEmailList() - form.list is not null');
        this.previewContacts = []; //this.form.recipients.map(r => ({...r}));
        this.form.list = null;
      }

      // MLD-7930 Ensure TinyMCE stays initialized when switching back to individual contacts
      //this.ensureTinyMCEInitialized();
    },
    async selectEmailList() {
      try {
        // MLD-7502
        const previewSubscribers = await mailcoachService.getActiveListSubscribers(this.form.list.id, 0, 100);
        this.form.recipients = [];
        this.previewContacts = previewSubscribers.map(s => s.contact);

        // MLD-7930 Ensure TinyMCE stays initialized when switching to list
        //this.ensureTinyMCEInitialized();
      } catch (error) {
        // console.warn('TinyMCE selectEmailList exception:', error);
        this.form.recipients = [];
        this.previewContacts = [];
      }
    },
    async fetchData() {
      await this.fetchTemplates();
      await this.fetchLists();
      await this.fetchPresentation();
      await this.fetchUserContact();
      this.fetchSpaces()
      this.fetchSnippets();
    },
    async fetchTemplates() {
      this.templatesLoading = true;
      const params = { // MLD-4653
        can_be_selected: true,
      }
      this.templates = await shareTemplateService.getTemplatesForUser(params)
      if (this.templateID) {
        const template = this.findTemplate(this.templateID);
        if (template) {
          await this.selectTemplate(template);
        }
      }
      this.templatesLoading = false;
    },
    removePresentation(presentation) {
      this.form.presentations = this.form.presentations.filter(presentationID => presentationID !== presentation.id);
    },
    removeAllPresentations() {
      this.form.presentations = [];
    },
    scrollToTop() {
      scrollingService.scrollToTop();
    },
    async fetchUserContact() { // MLD-6614
      if (this.contactID) {
        try {
          this.form.contact = await userContactService.getContact(this.contactID);
          await this.checkContact();
        } catch (e) {
          console.error('fetchUserContact exception', e.message);
        }
      }
    },
    async fetchPresentation() {
      if (this.presentationID) {
        return presentationService.getPresentation(this.presentationID)
          .then(async data => {
            this.presentation = data;
            this.form.presentations = [this.presentationID];

            if (this.presentation.share_template_id) { // MLD-6798
              const template = await shareTemplateService.getTemplate(this.presentation.share_template_id);
              if (template) {
                await this.selectTemplate(template);
              }
            }
          })
          .catch(err => {
            this.$toast.error(`There was a problem retrieving presentation ${DOMPurify.sanitize(this.presentationID)}.`);
            this.presentation = null;
            this.removeAllPresentations();
          })
      } else {
        this.presentation = null;
        return Promise.resolve();
      }
    },
    async deselectTemplate() {
      this.destroyTinyMCE();
      this.form.reset();
      this.form.clear();
      this.template = null;
      this.htmlContent = ''; // MLD-7930 Clear htmlContent
      this.contacts = [];
      this.customTags = []; // MLD-6454
      this.subjectLines = []; // MLD-6517
      this.search = '';
      await this.$store.dispatch('emailCart/setTemplate', null);
      await this.$store.dispatch('content/setMustExitProperly', false); // MLD-2947
    },
    async selectTemplate(template) {
      if (isObject(template) && isInteger(template.id)) {
        this.template = template = await shareTemplateService.getTemplate(template.id);
        await this.$store.dispatch('emailCart/setTemplate', template);
        this.form.subject = template.subject;
        this.htmlContent = template.html_body.replace('[INSERT LINKS HERE]', '{{ shared_links }}');
        this.form.html_body = this.htmlContent; //''; // MLD-7930 Keep empty, use htmlContent for TinyMCE
        this.form.template_id = template.id;

        // MLD-6464
        this.customTags = await shareTemplateService.getCustomTagsForTemplate(template.id);
        this.customTags.forEach(ct => {
          this.form.custom_tags.push()
        })

        // MLD-6517
        this.subjectLines = await shareTemplateService.getSubjectLinesFor(template.id);

        this.showInstructionsDialog = !!this.template.instructions;

        // MLD-3108 - should be FALSE for electron and iOS app, TRUE everywhere else.
        let mustExitProperly = this.isApp === false;
        await this.$store.dispatch('content/setMustExitProperly', mustExitProperly); // MLD-2947

        this.useTinyMCE = this.template.is_tinymce
          && this.template.is_editable;

        await this.initializeTinyMCE();

      } else {
        await this.deselectTemplate();
      }
    },
    resetEmailVerificationStatus() {
      this.email = null;
      this.emailVerificationStatus = null;
      this.didLastMessageBounce = false;
    },
    focusContactField() {
      this.$refs.contactCombo?.focus();
    },
    blurContactField() {
      this.$refs.contactCombo?.blur();
    },
    async addNewContact() {
      this.blurContactField();
      if (isString(this.search) && this.isEmailAddress(this.search)) {
        this.resetEmailVerificationStatus();
        this.email = this.search.toLowerCase().trim();
        try {
          this.addingContact = true;
          this.form.busy = true;
          await this.verifyEmail(this.email);
          if (isObject(this.emailVerificationStatus) && this.emailVerificationStatus.is_invalid === true) {
            this.form.busy = false;
            this.form.errors.set('email', 'Invalid email address.');
            return;
          } else {
            this.didLastMessageBounce = await emailVerificationService.didLastMessageBounce(this.email);
            if (this.didLastMessageBounce) {
              this.form.busy = false;
              this.form.errors.set('email', 'Invalid email address. The last message sent to this address bounced.');
              return;
            }
          }
          this.form.contact = await userContactService.createContactFromEmail(this.email);
          await this.checkContact();
        } catch (e) {
          console.error(e);
        } finally {
          this.form.busy = false;
          this.addingContact = false;
        }
      }
    },
    async checkContact() {
      this.resetEmailVerificationStatus();
      if (this.form.contact) {
        if (isObject(this.form.contact) && isInteger(this.form.contact.id)) {
          try {
            this.addingContact = true;
            let contactID = this.form.contact.id;
            const contact = await userContactService.getContact(contactID);
            if (contact.is_unsubscribed) {
              this.form.errors.set('contact', 'That email address is unsubscribed.');
              return;
            }
            if (this.canEmailSelf === false && contact.email === this.user.email) { // MLD-5505
              this.form.errors.set('contact', 'You cannot send emails to yourself.');
              return;
            }
            await this.verifyEmail(contact.email);
            if (this.emailVerificationStatus?.is_invalid) {
              return;
            }
            if (this.addRecipient(contact)) {
              this.form.errors.clear('contact');
              this.$toast.success(`Added ${contact.email}`);
            } else {
              this.form.errors.set('contact', 'That email address is already on the list');
            }
          } catch (e) {
            this.$toast.error('There was a problem selecting that contact.');
            this.form.contact = null;
            this.search = '';
          } finally {
            this.form.contact = null;
            this.search = '';
            this.addingContact = false;
            this.blurContactField();
          }
        }
      }
    },
    clearContactErrors() {
      this.form?.errors?.clear('contact');
    },
    async verifyEmail(email) { // MLD-6545
      if (isString(email) && this.isEmailAddress(email)) {
        this.email = email;
        this.emailVerificationStatus = await emailVerificationService.verifyEmail(email);
        return;
      }
      this.resetEmailVerificationStatus();
    },
    addRecipient(contact) {
      const exists = find(this.form.recipients, r => r.id === contact.id || r.email === contact.email);
      if (exists) {
        return false;
      }
      if (this.form.list) {
        this.deselectEmailList();
      }

      // MLD-7930 I had to do it this way to fix the Vue reactivity issue with TinyMCE.
      this.form.recipients.push(contact)
      this.previewContacts.push({
        id: contact.id,
        name: contact.name,
        email: contact.email,
        name_email: contact.name_email,
      });
      //this.previewContacts = previewContacts;
      // console.log('addRecipient() - previewContacts:', previewContacts);
      return true;
    },
    updateRecipient(recipient) {
      const index = findIndex(this.form.recipients, r => r.id === recipient.id);
      if (isInteger(index)) {
        const recipients = clone(this.form.recipients);
        recipients[index] = recipient;
        this.form.recipients = recipients;
      } else {
        this.form.recipients.push(recipient);
      }
    },
    removeRecipient(recipient) {
      this.form.recipients = this.form.recipients.filter(r => r.id !== recipient.id);
      this.previewContacts = this.previewContacts.filter(r => r.id !== recipient.id); // MLD-7502
    },
    removeAllRecipients() {
      // MLD-7502
      const recipientIDs = this.form.recipients.map(r => r.id);
      this.previewContacts = this.previewContacts.filter(r => !recipientIDs.includes(r.id));
      this.form.recipients = [];
    },
    clearSearchInput() {
      this.search = '';
    },
    searchContacts(query) {
      this.searchError = null;
      this.contactsLoading = true;
      const isEmail = validationService.isEmailValid(query);
      const form = {
        email: isEmail ? query : null,
        query: isEmail ? null : query,
        order_by: 'updated_at',
        order_dir: 'desc',
        limit: 30,
      }
      return userContactService.search(form, this.user)
        .then(data => {
          if (isArray(data.hits)) { // MLD-4320 - Meilisearch results
            this.contacts = data.hits;
          } else {
            this.contacts = data;
          }
        })
        .catch(err => { // MLD-4882
          this.searchError = err;
        })
        .finally(() => {
          this.contactsLoading = false;
        })
    },
    async promptForSchedule() { // MLD-6106
      this.scheduleDialog = true;
    },
    async cancelSchedule() { // MLD-6106
      this.form.scheduled_at = null;
      this.form.scheduled_date = null;
      this.form.scheduled_time = null;
      this.scheduleDialog = false;
    },
    async openScheduleSendDialog() { // MLD-6106
      this.form.scheduled_at = null;
      this.form.scheduled_date = moment().format('YYYY-MM-DD');
      this.form.scheduled_time = this.calculateStartTime().format('HH:mm');
    },
    calculateStartTime() {
      return moment().minutes(Math.ceil(moment().minutes() / this.scheduleMinutesInterval) * this.scheduleMinutesInterval);
    },
    async updateScheduledTime() { // MLD-6106
      if (this.isScheduleFormValid === false) {
        this.form.scheduled_time = '';
      }
    },
    async scheduleSend() { // MLD-6106
      this.scheduleError = null;
      if (this.isScheduleFormValid === false) {
        this.scheduleError = 'Invalid schedule time.'
        return;
      }
      this.isScheduled = true;
      await this.confirmSend();
      this.scheduleDialog = false;
    },
    async sendNow() {
      this.isScheduled = false;
      this.form.scheduled_at = moment.utc();
      this.showConfirmDialog();
    },
    showConfirmDialog() {
      this.confirmDialog = true;
      this.confirmed = false;
    },
    async confirmSend() {
      this.confirmed = true;
      await this.send();
    },
    cancelSend() {
      this.isScheduled = false;
      this.confirmed = false;
      this.confirmDialog = false;
    },
    buildCustomTags() {
      const customTags = this.customTags.map((ct, index) => { // MLD-6454
        return {
          id: ct.id,
          slug: ct.slug,
          tag_name: ct.tag_name,
          value: ct.is_crm_query ? 'crm_query_placeholder_value' : this.form.custom_tags[index], // MLD-7075 CRM queries need a hard coded value.
        };
      })
      if (customTags?.length > 0) {  // MLD-6454
        return customTags;
      }
      return null;
    },
    async send() {
      if (this.form.busy) {
        return;
      }
      if (this.isFormValid === false) {
        this.$toast.error('You have not completed the form.');
      }
      if (this.confirmed === false) {
        this.showConfirmDialog();
        return;
      }
      let content = (this.useTinyMCE && this.editor)
        ? this.editor.getContent()
        : this.htmlContent;

      content = content.replace('[INSERT LINKS HERE]', '{{ shared_links }}');

      const form = {
        template_id: this.form.template_id,
        presentations: this.form.presentations,
        list_id: this.form.list?.id, // MLD-7502
        user_contacts: this.form.recipients.map(r => r.id),
        scheduled_at: (this.computeScheduledAt ?? moment.utc()).format(),
        scheduled_timezone: this.form.scheduled_timezone,
        custom_message: this.form.custom_message,
        subject: this.form.subject,
        body: content,
      };

      if (this.crmRelatedObject && this.crmRelatedObjectID) { // MLD-6845
        form.crm_related_object = this.crmRelatedObject;
        form.crm_related_object_id = this.crmRelatedObjectID;
      }

      const customTags = this.buildCustomTags();
      if (isArray(customTags) && customTags.length > 0) {
        form.custom_tags = customTags;
      }

      try {
        this.form.startProcessing();
        const response = await axios.post(`/api/mailcoach/campaigns/build-and-send`, form)
        if (response.data.is_scheduled) {
          this.$toast.success(`Your email will be sent at the scheduled time.`);
        } else {
          this.$toast.success(`Your email will be sent shortly.`);
        }
        await this.$store.dispatch('content/setMustExitProperly', false); // MLD-2947
        await this.$router.push({name: `${this.$root.routePrefix}.mailcoach.campaign.show`, params: {campaignID: response.data.campaign_id}})
      } catch (e) {
        this.form.handleErrors(e); // MLD-7028
        if (e.response?.data?.message) { // MLD-7028
          this.$toast.error(DOMPurify.sanitize(e.response.data.message));
        } else {
          this.$toast.error('There was a problem sending your email.');
        }
      } finally {
        this.form.busy = false;
      }
    },
    async promptBeforeUnloading(event) { // MLD-2947
      if (this.mustExitProperly) {
        event.preventDefault();
        return event.returnValue = this.returnPrompt;
      }
    },
    closeInstructions() { // MLD-3031
      this.showInstructionsDialog = false;
    },
    async previewEmail() { // MLD-7224
      // MLD-7502
      const contact = this.preview_contact ?? this.previewContacts[0];
      this.form.startProcessing();
      const customTags = this.buildCustomTags();
      const form = {
        subject: this.form.subject,
        html_body: this.htmlContent,
        user_contact_id: contact.id,
        custom_tags: customTags ?? [],
        custom_message: this.form.custom_message,
        presentations: this.form.presentations,
        render: true,
      }
      try {
        const data = await shareTemplateService.render(this.template.id, form)
        let html = `<!DOCTYPE HTML PUBLIC "-//W3C//DTD XHTML 1.0 Transitional //EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd"><html xmlns="http://www.w3.org/1999/xhtml" lang="en"><head>${this.template?.html_head ?? ''}</head><body>${data.content}</body></html>`
        if (data.content?.startsWith('<body')) {
          html = `<!DOCTYPE HTML PUBLIC "-//W3C//DTD XHTML 1.0 Transitional //EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd"><html xmlns="http://www.w3.org/1999/xhtml" lang="en"><head>${this.template?.html_head ?? ''}</head>${data.content}</html>`
        }
        this.preview_content = html
        this.preview_from_address = data.from_address;
        this.preview_from_name = data.from_name;
        this.preview_subject = data.subject;
        this.preview_contact = contact;
        try {
          this.previewImage = await browserlessService.screenshotEmail(html, this.previewImageWidth); // MLD-7932
        } catch (error) {
          //this.preview_error = error;
          this.previewImage = null;
        }
        this.form.finishProcessing();
      } catch (e) {
        this.preview_error = e;
      } finally {
        this.form.busy = false;
        this.previewDialog = true;
      }
    },
    closePreviewDialog() { // MLD-7224
      this.resetPreview();
    },
    resetPreview() { // MLD-7224
      this.preview_content = null;
      this.preview_contact = null;
      this.preview_from_address = null;
      this.preview_from_name = null;
      this.preview_subject = null;
      this.previewDialog = false;
    },
  }
}
</script>
